import { TextField } from '@blueprinthq/joy'
import { Box, Button, Text, Textarea, useToast, VStack } from '@chakra-ui/react'
import { SendIcon } from '@icons'
import { trackEvent } from '@lib/clinician-tracking'
import { Field, Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import { useGrowsurfControllerSendInviteEmail } from '~/clinician-api'
import { DEFAULT_SOCIAL_COPY } from './social-sharing-module'

interface SendEmailTabProps {
  onCancel: () => void
  onSuccess?: () => void
}

interface FormValues {
  recipientName: string
  recipientEmail: string
  inviteMessage: string
}

const validationSchema = Yup.object().shape({
  recipientName: Yup.string(),
  recipientEmail: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  inviteMessage: Yup.string()
})

export const SendEmailTab: React.FC<SendEmailTabProps> = ({
  onCancel,
  onSuccess
}) => {
  const toast = useToast()

  const initialValues: FormValues = {
    recipientName: '',
    recipientEmail: '',
    inviteMessage: DEFAULT_SOCIAL_COPY
  }

  const { mutate: sendInviteEmail, isLoading: isSendingEmail } =
    useGrowsurfControllerSendInviteEmail({
      mutation: {
        onSuccess: () => {
          toast({
            title: 'Invitation sent!',
            description: 'Your invitation has been sent successfully.',
            status: 'success',
            duration: 5000,
            isClosable: true
          })

          trackEvent('Referral V3 -> Email Invite Sent', {
            shareMethod: 'email'
          })

          // Notify parent of success and close
          if (onSuccess) onSuccess()
          onCancel()
        },
        onError: error => {
          console.error('Failed to send invite email', error)
          toast({
            title: "Couldn't send invitation",
            description:
              'There was an error sending your invitation. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
      }
    })

  const handleSubmit = (values: FormValues) => {
    sendInviteEmail({
      data: {
        referredClinicianName: values.recipientName,
        referredClinicianEmail: values.recipientEmail,
        referralInviteMessage: values.inviteMessage
      }
    })
  }

  return (
    <Box overflowY="auto" maxHeight="472px">
      <VStack spacing={4} align="stretch" p={4}>
        <Box textAlign="center" mb={6}>
          <Text fontSize="24px" fontWeight="bold">
            Enter the recipient details
          </Text>
          <Text mt={2}>Write a custom message if you want 😊</Text>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isValid, dirty }) => (
            <Form style={{ width: '100%' }}>
              <VStack
                spacing={
                  errors.recipientEmail && touched.recipientEmail ? 6 : 4
                }
                width="100%"
              >
                <Field name="recipientName">
                  {({ field }: { field: any }) => (
                    <TextField
                      {...field}
                      label="Recipient name (optional)"
                      width="100%"
                      bg="white"
                    />
                  )}
                </Field>

                <Field name="recipientEmail">
                  {({ field }: { field: any }) => (
                    <TextField
                      {...field}
                      autoFocus
                      label="Recipient email"
                      isInvalid={
                        errors.recipientEmail && touched.recipientEmail
                      }
                      errorText={errors.recipientEmail}
                      isRequired
                      width="100%"
                      bg="white"
                    />
                  )}
                </Field>

                <Field name="inviteMessage">
                  {({ field }: { field: any }) => (
                    <Textarea
                      {...field}
                      placeholder="Message (optional)"
                      resize="vertical"
                      minH="8rem"
                      pt="10px"
                      bg="white"
                      borderColor="var(--chakra-colors-light_gray)"
                      width="100%"
                    />
                  )}
                </Field>

                <Button
                  type="submit"
                  colorScheme="blue"
                  isFullWidth
                  height="50px"
                  isLoading={isSendingEmail}
                  leftIcon={<SendIcon />}
                  isDisabled={!isValid || !dirty}
                >
                  Send
                </Button>

                <Button
                  variant="outline"
                  isFullWidth
                  height="50px"
                  onClick={e => {
                    trackEvent('Referral V3 -> Email Invite Cancelled', {
                      shareMethod: 'email'
                    })
                    onCancel()
                  }}
                  isDisabled={isSendingEmail}
                >
                  Cancel
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    </Box>
  )
}

export default SendEmailTab
